import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl, Typography } from '@mui/material';
import { DatePickerToolbarProps, StaticDatePicker } from '@mui/x-date-pickers';
import { format, isBefore, isEqual, isValid as isValidDate } from 'date-fns';
import {
  Controller,
  ControllerRenderProps,
  FieldErrors,
  useForm,
} from 'react-hook-form';
import * as yup from 'yup';

type FormValues = {
  initialDate: Date;
  finalDate: Date;
};

type DatePickerModalProps = Omit<
  ModalProps,
  'title' | 'subtitle' | 'children' | 'onClose'
> & {
  onClose: (values?: FormValues) => void;
  initialDate?: string | Date;
  finalDate?: string | Date;
};

export function DatePickerModal({
  onClose,
  initialDate,
  finalDate,
  ...props
}: DatePickerModalProps) {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      initialDate: initialDate ? new Date(initialDate) : undefined,
      finalDate: finalDate ? new Date(finalDate) : undefined,
    },
    mode: 'onChange',
    resolver: yupResolver(
      yup
        .object()
        .shape({
          initialDate: yup
            .date()
            .typeError('Invalid date')
            .required()
            .defined(),
          finalDate: yup.date().typeError('Invalid date').required().defined(),
        })
        .required()
        .test(({ initialDate, finalDate }) => {
          if (
            !isEqual(initialDate, finalDate) &&
            !isBefore(initialDate, finalDate)
          ) {
            throw new yup.ValidationError([
              new yup.ValidationError(
                'Final date must be after initial date',
                finalDate,
                'finalDate',
              ),
              new yup.ValidationError(
                'Initial date must be before final date',
                initialDate,
                'initialDate',
              ),
            ]);
          }

          return true;
        }),
    ),
  });

  return (
    <Modal
      title="Pick a date range"
      showCloseButton
      onClose={() => onClose(undefined)}
      {...props}
    >
      <form onSubmit={handleSubmit(onClose)} id="date-picker-modal-form">
        <div className="flex gap-6">
          {(
            [
              { name: 'initialDate', label: 'Start date' },
              { name: 'finalDate', label: 'End date' },
            ] as const
          ).map(({ name, label }) => (
            <FormControl key={name} hiddenLabel aria-label={label}>
              <Controller
                control={control}
                name={name}
                render={({ field }) => (
                  <StaticDatePicker
                    {...field}
                    value={field.value ?? null}
                    onChange={value => {
                      field.onChange(value);
                      field.onBlur(); // Manually trigger onBlur to mark field as touched
                      handleSubmit(onClose)();
                    }}
                    slotProps={{
                      actionBar: {
                        sx: {
                          display: 'none',
                        },
                      },
                      layout: {
                        classes: {
                          contentWrapper:
                            'bg-light-gray border border-t-0 rounded-b-lg',
                        },
                      },
                    }}
                    slots={{
                      toolbar: props => (
                        <CalendarToolbar
                          {...props}
                          field={field}
                          errors={errors}
                        />
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          ))}
        </div>
      </form>
    </Modal>
  );
}

type CalendarToolbarProps = {
  errors: FieldErrors<FormValues>;
  field: ControllerRenderProps<FormValues, 'initialDate' | 'finalDate'>;
} & DatePickerToolbarProps;

function CalendarToolbar({ errors, field }: CalendarToolbarProps) {
  return (
    <div className="flex flex-col col-span-3 px-6 py-3 border rounded-lg rounded-b-none">
      <Typography variant="caption">
        {field.name === 'initialDate' ? 'Start date' : 'End date'}
      </Typography>
      <Typography>
        {isValidDate(field.value) ? format(field.value, 'MMM d') : '---'}
      </Typography>
      <Typography variant="caption" color="error">
        {errors[field.name]?.message}
      </Typography>
    </div>
  );
}
